import React, { Fragment, useState } from "react";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import { X, ChevronDown, Check } from "lucide-react";
import ModalV2 from "../../../../../shared/components/modalV2";
import { GREEN_CLOSE_BTN } from "../../../../../assets";

interface MakeQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { receiver: string; query: string }) => void;
}

const RECEIVERS = [
  { id: 1, name: "Legal Officer" },
  { id: 2, name: "Registrar" },
  { id: 3, name: "Judge" },
];

const MakeQueryModal: React.FC<MakeQueryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [selectedReceiver, setSelectedReceiver] = useState(RECEIVERS[0]);
  const [query, setQuery] = useState("");

  const handleSubmit = () => {
    onSubmit({ receiver: selectedReceiver.name, query });
    onClose();
  };

  return (
    <ModalV2
      isOpen={isOpen}
      isClose={onClose}
      maxWidth="w-[428px]"
      edges="rounded-2xl"
    >
      <div className="p-8 flex w-full flex-col gap-y-6 relative">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-[#98A2B3] hover:text-gray-600 transition-colors"
        >
          <img src={GREEN_CLOSE_BTN} />
        </button>

        <h2 className="text-2xl font-bold text-[#1A1A1A] text-center">
          Make a new query
        </h2>

        <div className="flex flex-col gap-y-6 w-full">
          {/* Select Receiver */}
          <div className="flex flex-col items-start gap-y-2 w-full">
            <label className="text-sm text-[#344054] font-medium">
              Select Receiver
            </label>
            <Listbox value={selectedReceiver} onChange={setSelectedReceiver}>
              <div className="relative mt-1 w-full">
                <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-3 pl-4 pr-10 text-left border border-[#D0D5DD] focus:outline-none focus-visible:border-[#00B074] focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                  <span className="block truncate text-[#667085]">
                    {selectedReceiver.name}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
                    {RECEIVERS.map((person, personIdx) => (
                      <Listbox.Option
                        key={personIdx}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active
                              ? "bg-[#E6F7F1] text-[#00B074]"
                              : "text-gray-900"
                          }`
                        }
                        value={person}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? "font-medium" : "font-normal"
                              }`}
                            >
                              {person.name}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#00B074]">
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          {/* Query Input */}
          <div className="flex flex-col items-start gap-y-2">
            <label className="text-sm text-[#344054] font-medium">Query</label>
            <textarea
              rows={5}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Input"
              className="w-full border border-[#D0D5DD] rounded-lg p-4 text-sm placeholder:text-[#98A2B3] focus:outline-none focus:border-[#00B074] resize-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 mt-2">
          <button
            onClick={onClose}
            className="w-full bg-[#E4E7EC] text-[#1A1A1A] rounded-lg py-3 font-medium hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="w-full bg-[#00B074] text-white rounded-lg py-3 font-medium hover:bg-[#009b65] transition-colors"
          >
            Submit
          </button>
        </div>
      </div>
    </ModalV2>
  );
};

export default MakeQueryModal;
